/* eslint-disable @typescript-eslint/no-confusing-void-expression, multiline-ternary */
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Avatar,
  Box,
  Button,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  VStack,
  useToast
} from '@chakra-ui/react'
import { SearchIcon } from '@chakra-ui/icons'
import { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { type User } from 'firebase/auth'
import {
  type Appointment,
  type AppointmentSettings,
  type AppointmentStatus,
  type AppointmentVisitType
} from '../../../../../model/appointment'
import { type Nationality, type Patient } from '../../../../../model/patient'
import CountrySelect from '../../../../../components/common/form/CountrySelect'
import {
  getAppointmentService,
  getPatientService
} from '../../../../../services/Services'
import {
  clinicDateTimeToTimestamp,
  timestampToClinicInput,
  validateAppointmentSlot
} from '../../../../../utils/appointmentUtils'

interface Props {
  isOpen: boolean
  onClose: () => void
  ownerId: string
  actor: Pick<User, 'uid'> | { uid: string }
  settings: AppointmentSettings
  appointment?: Appointment | null
  initialStart?: number
}

interface FormState {
  patientId: string
  date: string
  time: string
  duration: Appointment['duration']
  visitType: AppointmentVisitType
  status: AppointmentStatus
  notes: string
}

function initialForm(
  settings: AppointmentSettings,
  appointment?: Appointment | null,
  initialStart?: number
): FormState {
  const start = appointment?.start ?? initialStart ?? Date.now()
  const input = timestampToClinicInput(start, settings.timezone)
  return {
    patientId: appointment?.patientId ?? '',
    date: input.date,
    time: input.time,
    duration: appointment?.duration ?? settings.defaultDuration,
    visitType: appointment?.visitType ?? 'visit',
    status: appointment?.status ?? 'scheduled',
    notes: appointment?.notes ?? ''
  }
}

export default function AppointmentModal({
  isOpen,
  onClose,
  ownerId,
  actor,
  settings,
  appointment,
  initialStart
}: Props) {
  const { formatMessage, formatTime, locale } = useIntl()
  const toast = useToast()
  const [form, setForm] = useState<FormState>(() =>
    initialForm(settings, appointment, initialStart)
  )
  const [selectedPatient, setSelectedPatient] = useState<
    Pick<Patient, 'id' | 'name' | 'phoneNumber'> | undefined
  >()
  const [patientSearch, setPatientSearch] = useState('')
  const [patientResults, setPatientResults] = useState<Patient[]>([])
  const [searchingPatients, setSearchingPatients] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [conflicts, setConflicts] = useState<Appointment[]>([])
  const [quickCreate, setQuickCreate] = useState(false)
  const [quickPatient, setQuickPatient] = useState({
    name: '',
    phoneNumber: '',
    dateOfBirth: '',
    sex: 'M' as 'M' | 'F',
    nationality: 'EG' as Nationality
  })

  useEffect(() => {
    if (!isOpen) return
    let active = true
    setForm(initialForm(settings, appointment, initialStart))
    setError(null)
    setConflicts([])
    setQuickCreate(false)
    setPatientSearch('')
    setPatientResults([])
    setSelectedPatient(
      appointment
        ? {
            id: appointment.patientId,
            name: appointment.patientName,
            phoneNumber: ''
          }
        : undefined
    )
    if (appointment) {
      void getPatientService()
        .getOnePatient({ patientId: appointment.patientId })
        .then((patient) => {
          if (active && patient) setSelectedPatient(patient)
        })
    }
    return () => {
      active = false
    }
  }, [appointment, formatMessage, initialStart, isOpen, ownerId, settings])

  useEffect(() => {
    const term = patientSearch.trim()
    if (!isOpen || term.length < 2 || selectedPatient) {
      setPatientResults([])
      setSearchingPatients(false)
      return
    }

    setSearchingPatients(true)
    let active = true
    const timeout = window.setTimeout(() => {
      void getPatientService()
        .searchPatients({ ownerId, searchTerm: term })
        .then((results) => {
          if (active) setPatientResults(results)
        })
        .catch(() => {
          if (active) setPatientResults([])
        })
        .finally(() => {
          if (active) setSearchingPatients(false)
        })
    }, 250)

    return () => {
      active = false
      window.clearTimeout(timeout)
    }
  }, [isOpen, ownerId, patientSearch, selectedPatient])

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((current) => ({ ...current, [key]: value }))
    setError(null)
    setConflicts([])
  }

  const createPatient = async () => {
    if (!quickPatient.name.trim() || !quickPatient.dateOfBirth) {
      setError(formatMessage({ id: 'calendar.quickPatientRequired' }))
      return
    }
    setSaving(true)
    try {
      const created = await getPatientService().createPatient({
        ownerId,
        name: quickPatient.name.trim(),
        phoneNumber: quickPatient.phoneNumber.trim(),
        dateOfBirth: quickPatient.dateOfBirth,
        sex: quickPatient.sex,
        nationality: quickPatient.nationality,
        address: ''
      })
      setSelectedPatient(created)
      update('patientId', created.id)
      setQuickCreate(false)
      setQuickPatient({
        name: '',
        phoneNumber: '',
        dateOfBirth: '',
        sex: 'M',
        nationality: 'EG'
      })
    } catch {
      setError(formatMessage({ id: 'calendar.quickPatientError' }))
    } finally {
      setSaving(false)
    }
  }

  const save = async (forceConflict = false) => {
    if (!selectedPatient) {
      setError(formatMessage({ id: 'calendar.patientRequired' }))
      return
    }
    if (!form.date || !form.time) {
      setError(formatMessage({ id: 'calendar.invalidDateTime' }))
      return
    }
    let start: number
    try {
      start = clinicDateTimeToTimestamp(form.date, form.time, settings.timezone)
    } catch {
      setError(formatMessage({ id: 'calendar.invalidDateTime' }))
      return
    }
    const slotError = validateAppointmentSlot(start, form.duration, settings)
    if (slotError) {
      setError(
        formatMessage({
          id:
            slotError === 'closed-day'
              ? 'calendar.closedDayError'
              : 'calendar.outsideHoursError'
        })
      )
      return
    }

    setSaving(true)
    try {
      const found = await getAppointmentService().findConflicts(
        ownerId,
        start,
        form.duration,
        appointment?.id
      )
      if (found.length > 0 && !forceConflict) {
        setConflicts(found)
        return
      }
      const payload = {
        ownerId,
        patientId: selectedPatient.id,
        patientName: selectedPatient.name,
        start,
        duration: form.duration,
        visitType: form.visitType,
        notes: form.notes,
        status: form.status
      }
      if (appointment) {
        await getAppointmentService().updateAppointment(
          appointment.id,
          payload,
          actor.uid
        )
      } else {
        await getAppointmentService().createAppointment(payload, actor.uid)
      }
      toast({
        title: formatMessage({ id: 'calendar.saved' }),
        status: 'success',
        duration: 2500
      })
      onClose()
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : formatMessage({ id: 'calendar.saveError' })
      )
    } finally {
      setSaving(false)
    }
  }

  const appointmentAction = async (
    action: 'check-in' | 'cancel' | 'no-show' | 'delete'
  ) => {
    if (!appointment) return
    if (
      action === 'delete' &&
      !window.confirm(formatMessage({ id: 'calendar.deleteConfirm' }))
    ) {
      return
    }
    setSaving(true)
    try {
      if (action === 'check-in') {
        await getAppointmentService().checkIn(appointment.id, actor.uid)
      } else if (action === 'cancel') {
        await getAppointmentService().cancelAppointment(
          appointment.id,
          actor.uid
        )
      } else if (action === 'delete') {
        await getAppointmentService().deleteAppointment(appointment.id)
      } else {
        await getAppointmentService().updateStatus(
          appointment.id,
          'no-show',
          actor.uid
        )
      }
      onClose()
    } catch (actionError) {
      setError(
        actionError instanceof Error
          ? actionError.message
          : formatMessage({ id: 'calendar.actionError' })
      )
    } finally {
      setSaving(false)
    }
  }

  const canEdit =
    !appointment || ['scheduled', 'confirmed'].includes(appointment.status)

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      placement={locale.startsWith('ar') ? 'left' : 'right'}
    >
      <DrawerOverlay backdropFilter="blur(2px)" />
      <DrawerContent>
        <DrawerHeader borderBottomWidth="1px" borderColor="gray.200" py={5}>
          <Text fontSize="lg" fontWeight="700">
            {formatMessage({
              id: appointment
                ? 'calendar.editAppointment'
                : 'calendar.newAppointment'
            })}
          </Text>
          <Text mt={1} fontSize="xs" fontWeight="normal" color="gray.500">
            {settings.timezone}
          </Text>
        </DrawerHeader>
        <DrawerCloseButton top={4} />
        <DrawerBody p={{ base: 4, md: 6 }}>
          <VStack spacing={4} align="stretch">
            {error && (
              <Alert status="error">
                <AlertIcon />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            <FormControl isRequired isDisabled={!canEdit}>
              <FormLabel>{formatMessage({ id: 'calendar.patient' })}</FormLabel>
              {selectedPatient ? (
                <Flex
                  align="center"
                  gap={3}
                  borderWidth="1px"
                  borderColor="gray.200"
                  borderRadius="10px"
                  bg="gray.50"
                  p={3}
                >
                  <Avatar
                    name={selectedPatient.name}
                    size="sm"
                    bg="gray.200"
                    color="gray.800"
                  />
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="650" noOfLines={1}>
                      {selectedPatient.name}
                    </Text>
                    {selectedPatient.phoneNumber && (
                      <Text fontSize="xs" color="gray.500">
                        {selectedPatient.phoneNumber}
                      </Text>
                    )}
                  </Box>
                  {canEdit && (
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setSelectedPatient(undefined)
                        update('patientId', '')
                      }}
                    >
                      {formatMessage({ id: 'calendar.changePatient' })}
                    </Button>
                  )}
                </Flex>
              ) : (
                <Box>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" boxSize="14px" />
                    </InputLeftElement>
                    <Input
                      aria-label={formatMessage({
                        id: 'calendar.searchPatients'
                      })}
                      value={patientSearch}
                      placeholder={formatMessage({
                        id: 'calendar.searchPatients'
                      })}
                      autoComplete="off"
                      onChange={(event) => setPatientSearch(event.target.value)}
                    />
                    {searchingPatients && (
                      <InputRightElement>
                        <Spinner size="xs" />
                      </InputRightElement>
                    )}
                  </InputGroup>
                  {patientSearch.trim().length < 2 && (
                    <Text mt={1.5} fontSize="xs" color="gray.500">
                      {formatMessage({ id: 'calendar.patientSearchHint' })}
                    </Text>
                  )}
                  {patientSearch.trim().length >= 2 &&
                    !searchingPatients &&
                    patientResults.length === 0 && (
                      <Text mt={2} fontSize="sm" color="gray.500">
                        {formatMessage({ id: 'calendar.noPatientMatches' })}
                      </Text>
                    )}
                  {patientResults.length > 0 && (
                    <VStack
                      mt={2}
                      spacing={0}
                      align="stretch"
                      borderWidth="1px"
                      borderColor="gray.200"
                      borderRadius="10px"
                      overflow="hidden"
                    >
                      {patientResults.map((patient) => (
                        <Button
                          key={patient.id}
                          variant="ghost"
                          borderRadius={0}
                          h="auto"
                          px={3}
                          py={2.5}
                          justifyContent="flex-start"
                          textAlign="start"
                          borderBottomWidth="1px"
                          borderColor="gray.100"
                          _last={{ borderBottomWidth: 0 }}
                          onClick={() => {
                            setSelectedPatient(patient)
                            update('patientId', patient.id)
                            setPatientSearch('')
                            setPatientResults([])
                          }}
                        >
                          <Avatar name={patient.name} size="xs" me={3} />
                          <Box>
                            <Text fontSize="sm" fontWeight="600">
                              {patient.name}
                            </Text>
                            {patient.phoneNumber && (
                              <Text fontSize="xs" color="gray.500">
                                {patient.phoneNumber}
                              </Text>
                            )}
                          </Box>
                        </Button>
                      ))}
                    </VStack>
                  )}
                </Box>
              )}
            </FormControl>
            {canEdit && !quickCreate && (
              <Button
                size="sm"
                alignSelf="flex-start"
                variant="link"
                onClick={() => {
                  setQuickCreate(true)
                }}
              >
                {formatMessage({ id: 'calendar.quickCreatePatient' })}
              </Button>
            )}
            {quickCreate && (
              <Box borderWidth="1px" borderRadius="md" p={4}>
                <Text fontWeight="semibold" mb={3}>
                  {formatMessage({ id: 'calendar.newPatient' })}
                </Text>
                <VStack spacing={3}>
                  <Input
                    aria-label={formatMessage({ id: 'calendar.patientName' })}
                    placeholder={formatMessage({ id: 'calendar.patientName' })}
                    value={quickPatient.name}
                    onChange={(event) => {
                      setQuickPatient((current) => ({
                        ...current,
                        name: event.target.value
                      }))
                    }}
                  />
                  <SimpleGrid columns={2} spacing={3} w="full">
                    <Input
                      type="date"
                      aria-label={formatMessage({ id: 'calendar.dateOfBirth' })}
                      value={quickPatient.dateOfBirth}
                      onChange={(event) => {
                        setQuickPatient((current) => ({
                          ...current,
                          dateOfBirth: event.target.value
                        }))
                      }}
                    />
                    <Select
                      value={quickPatient.sex}
                      onChange={(event) => {
                        setQuickPatient((current) => ({
                          ...current,
                          sex: event.target.value as 'M' | 'F'
                        }))
                      }}
                    >
                      <option value="M">
                        {formatMessage({ id: 'calendar.male' })}
                      </option>
                      <option value="F">
                        {formatMessage({ id: 'calendar.female' })}
                      </option>
                    </Select>
                  </SimpleGrid>
                  <Input
                    placeholder={formatMessage({ id: 'calendar.phone' })}
                    value={quickPatient.phoneNumber}
                    onChange={(event) => {
                      setQuickPatient((current) => ({
                        ...current,
                        phoneNumber: event.target.value
                      }))
                    }}
                  />
                  <Box w="full">
                    <CountrySelect
                      label={formatMessage({
                        id: 'patientForm.nationality',
                        defaultMessage: 'Nationality'
                      })}
                      value={quickPatient.nationality}
                      onChange={(nationality) => {
                        setQuickPatient((current) => ({
                          ...current,
                          nationality
                        }))
                      }}
                    />
                  </Box>
                  <HStack alignSelf="flex-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setQuickCreate(false)
                      }}
                    >
                      {formatMessage({ id: 'calendar.cancel' })}
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => {
                        void createPatient()
                      }}
                      isLoading={saving}
                    >
                      {formatMessage({ id: 'calendar.createPatient' })}
                    </Button>
                  </HStack>
                </VStack>
              </Box>
            )}
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4}>
              <FormControl
                id="appointment-date"
                isRequired
                isDisabled={!canEdit}
              >
                <FormLabel>{formatMessage({ id: 'calendar.date' })}</FormLabel>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(event) => {
                    update('date', event.target.value)
                  }}
                />
              </FormControl>
              <FormControl
                id="appointment-time"
                isRequired
                isDisabled={!canEdit}
              >
                <FormLabel>{formatMessage({ id: 'calendar.time' })}</FormLabel>
                <Input
                  type="time"
                  value={form.time}
                  onChange={(event) => {
                    update('time', event.target.value)
                  }}
                />
              </FormControl>
              <FormControl id="appointment-duration" isDisabled={!canEdit}>
                <FormLabel>
                  {formatMessage({ id: 'calendar.duration' })}
                </FormLabel>
                <Select
                  value={form.duration}
                  onChange={(event) => {
                    update(
                      'duration',
                      Number(event.target.value) as Appointment['duration']
                    )
                  }}
                >
                  {[15, 30, 45, 60].map((duration) => (
                    <option key={duration} value={duration}>
                      {duration} {formatMessage({ id: 'calendar.minutes' })}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl id="appointment-type" isDisabled={!canEdit}>
                <FormLabel>{formatMessage({ id: 'calendar.type' })}</FormLabel>
                <Select
                  value={form.visitType}
                  onChange={(event) => {
                    update(
                      'visitType',
                      event.target.value as AppointmentVisitType
                    )
                  }}
                >
                  <option value="visit">
                    {formatMessage({ id: 'calendar.visit' })}
                  </option>
                  <option value="follow-up">
                    {formatMessage({ id: 'calendar.followUp' })}
                  </option>
                </Select>
              </FormControl>
              <FormControl id="appointment-status" isDisabled={!canEdit}>
                <FormLabel>
                  {formatMessage({ id: 'calendar.status' })}
                </FormLabel>
                <Select
                  value={form.status}
                  onChange={(event) => {
                    update('status', event.target.value as AppointmentStatus)
                  }}
                >
                  <option value="scheduled">
                    {formatMessage({ id: 'calendar.status.scheduled' })}
                  </option>
                  <option value="confirmed">
                    {formatMessage({ id: 'calendar.status.confirmed' })}
                  </option>
                </Select>
              </FormControl>
            </SimpleGrid>
            <FormControl isDisabled={!canEdit}>
              <FormLabel>{formatMessage({ id: 'calendar.notes' })}</FormLabel>
              <Textarea
                value={form.notes}
                onChange={(event) => {
                  update('notes', event.target.value)
                }}
              />
            </FormControl>
            {appointment && (
              <Text fontSize="sm" color="gray.500">
                {formatMessage({ id: 'calendar.currentStatus' })}:{' '}
                {formatMessage({ id: `calendar.status.${appointment.status}` })}
              </Text>
            )}
            {conflicts.length > 0 && (
              <Alert status="warning" alignItems="flex-start">
                <AlertIcon />
                <Box>
                  <Text fontWeight="semibold">
                    {formatMessage({ id: 'calendar.conflictsFound' })}
                  </Text>
                  {conflicts.map((conflict) => (
                    <Text key={conflict.id} fontSize="sm">
                      {conflict.patientName} —{' '}
                      {formatTime(conflict.start, {
                        timeZone: settings.timezone,
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </Text>
                  ))}
                  <Button
                    mt={2}
                    size="sm"
                    colorScheme="orange"
                    onClick={() => {
                      void save(true)
                    }}
                    isLoading={saving}
                  >
                    {formatMessage({ id: 'calendar.saveAnyway' })}
                  </Button>
                </Box>
              </Alert>
            )}
            {appointment && (
              <>
                <Divider />
                <HStack flexWrap="wrap">
                  {['scheduled', 'confirmed'].includes(appointment.status) && (
                    <Button
                      size="sm"
                      colorScheme="green"
                      onClick={() => {
                        void appointmentAction('check-in')
                      }}
                    >
                      {formatMessage({ id: 'calendar.checkIn' })}
                    </Button>
                  )}
                  {['scheduled', 'confirmed'].includes(appointment.status) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        void appointmentAction('no-show')
                      }}
                    >
                      {formatMessage({ id: 'calendar.noShow' })}
                    </Button>
                  )}
                  {['scheduled', 'confirmed', 'checked-in'].includes(
                    appointment.status
                  ) && (
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="orange"
                      onClick={() => {
                        void appointmentAction('cancel')
                      }}
                    >
                      {formatMessage({ id: 'calendar.cancelAppointment' })}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={() => {
                      void appointmentAction('delete')
                    }}
                  >
                    {formatMessage({ id: 'calendar.deletePermanently' })}
                  </Button>
                </HStack>
              </>
            )}
          </VStack>
        </DrawerBody>
        <DrawerFooter gap={2} borderTopWidth="1px" borderColor="gray.200">
          <Button variant="ghost" onClick={onClose}>
            {formatMessage({ id: 'calendar.close' })}
          </Button>
          {canEdit && (
            <Button
              colorScheme="blue"
              onClick={() => {
                void save(false)
              }}
              isLoading={saving}
            >
              {formatMessage({ id: 'calendar.save' })}
            </Button>
          )}
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  )
}
